// ViewSwitcher — the five view-switcher buttons (NIGHT_SHIFT.md item 14, 2026-07-23).
// Moved from the app banner (far right, past a full-width traverse from the tree
// on the far left) to the TOP OF THE TREE — Alex: "I found myself moving from the
// left to right, left to right, left to right." Two rows, grouped by kind:
//   row 1 — project views: 'projects' ("Internal") / 'projectsByCustomer' ("Customer")
//   row 2 — item views:    'catalog' / 'products' / 'onshape'
// View KEYS are unchanged and still persisted in localStorage (App.jsx) — this
// component only changes LABELS and POSITION, never the keys.
//
// Row 1 drops the word "Project" — MEASURED (2026-07-21, 13px Arial, 24px button
// padding, tree = 240px default): "Project (internal)" + "Project (by Customer)"
// = 273px, does NOT fit; "Internal" + "Customer" = 151px, fits comfortably. No
// loss of meaning: the active-project picker in the banner already names the
// project, so repeating "Project" twice right under it was noise.
//
// ⚠ ROW 2 NO LONGER FITS ON ONE LINE, AND HAS NOT FOR A WHILE. Re-measured in
// the running app 2026-08-27 at the 240px default tree: content 290px against
// 210px available, so it renders as two lines. The icons added that day cost
// ~55px of it — but the row was ALREADY over and already wrapping before them
// (Alex's own screenshot the same day shows Catalog+Products on one line and
// 3D Model on the next). The 229px/11px-spare figure above was measured at a
// different tree width and is kept only to show what changed.
//
// The row WRAPS rather than clips, so this is a look, not a break. If one line
// is wanted back, the lever is a shorter label — not dropping the icons, which
// are what makes this row read as a group.
//
// Root-select preservation: the OLD `treeTitle` ("Catalog"/"Products"/…) on
// HierarchyWorkspace selected the tree's root on click; this switcher now
// occupies that slot. Since App.jsx mounts exactly one view at a time, the only
// button that can be "active" here is this screen's own — so clicking the
// active button re-runs root-select (`onSelectRoot`) instead of re-navigating,
// which reproduces the old treeTitle-click behavior without a 3rd row or extra
// control (both would risk the tight row-2 fit above).
//
// Rendered via HierarchyWorkspace's `treeHeader` slot, which HierarchyWorkspace
// suppresses whenever `hideNav` is set — so the Onshape screen's embedded
// Catalog/Products (rendered with hideNav) never sprout a second switcher.

// Row 3 — AUDIT (Alex, 2026-07-31): "can a menu be added somewhere in the app for
// easy access (and for me to not search) to the Schema & Surfaces". /schema and
// /surfaces are server-RENDERED pages, not app views, so they are anchors and not
// SwitchButtons: they can never be the `active` view, and styling them as view
// buttons would promise a switch that does not happen. They open in a new tab so
// the app's tree/selection state survives the trip.
//
// Fits comfortably: MEASURED in the running app on :8012, 2026-07-31 -- the row's
// CONTENT is 167px of the 247px available (80px spare), against row 2's 235px
// (12px spare). So this row is not the constrained one; row 2 still is. Measure
// the content, not the row: these rows are flex children of a column and stretch
// to the full width, so the row's own boundingRect always equals the container
// and says nothing about whether it fits.
// The ↗ marks them as leaving the app; it is a glyph, not a colour, because Alex
// is colour-blind (LINK from icons.js, the same glyph used for every other
// leaves-the-app link).

import { NAVY, BTN_TAB_RADIUS } from '../theme-tokens.js';
import { LINK, CATALOG, PRODUCT, Model3DMark } from '../icons.js';
import { useContext } from 'react';
import { useAuthGroup } from '../AuthGate.jsx';
import { WorkingInContext } from '../can-change.js';
import { NavModeContext } from '../nav-mode.js';

// 'customer' is the CUSTOMER-FACING screen (CustomerView), not to be confused
// with 'projectsByCustomer', which is the internal list grouped by customer and
// has owned the word "Customer" here since 2026-07-03. Rather than rename an
// existing button, the new one is labelled by what it PRODUCES -- a proposal --
// which is also the shorter word, and row 1 is the row with spare width
// (measured 151px of 247px, so ~86px for "Proposal" fits; row 2 is the tight
// one). The row already wraps rather than clipping if a future label is longer.
// ⚠ "CUSTOMER" MEANT TWO THINGS, ONE ABOVE THE OTHER, AND IT COST ALEX AN AFTERNOON.
// This row used to be ['projects','Internal'] + ['projectsByCustomer','Customer'] — where
// "Customer" opened YOUR OWN PROJECTS GROUPED BY CUSTOMER, an internal list, while the row
// directly beneath it held the screens that genuinely are the customer's. Alex,
// 2026-08-16: "I do not understand how the customer main screen works because he moved
// directly to internal" — of course, because the button labelled Customer was not it.
//
// `projectsByCustomer` is REMOVED from the switcher, not deleted: "project should only be
// changed (for now) in the top drop down list", so the banner's project picker is the one
// way to change project and a second navigation for it was the redundancy underneath the
// confusion. The view key and its screen still exist and can be restored to this array.
// D9 (PROJECTS_AND_TEMPLATES.md, Alex 2026-10-13/14) REPLACED ROWS 1 AND 1B.
// "Internal" became FUNCTIONS (the same screen, named by what it shows: the function
// tree, its placements and their cost), and "Customer" left the menu for the
// AudienceToggle in the top bar ("Can it be a toggle button instead ... beside
// 'Working in'"). So the project row is now Functions + Proposal, and in Customer
// view the menu is that row alone, pointed at the customer's own screens.
//
// ⚠ LABELS ONLY, AS BEFORE — the keys ('projects', 'customerConfigure', 'customer')
// are persisted in localStorage and appear in deep links.
//
// Both are PROJECT views: Standard is a library with no functions, no placements and
// no customer. On Standard, Proposal is greyed out with the reason in words; Functions
// stays open and shows the project list, because that list is where a project is
// created or copied (Alex chose this 2026-10-14 over greying it out, which left no way
// to make a first project).
//
// Alex, 2026-10-14: "catalog, product, and function on the same line. And then the
// rest on the second line." So the three lenses of one project's data share row 1
// (Functions gets no icon: icons mark the item views, see the note below), and
// Proposal + 3D Model, the two outputs, share row 2.
const ROW_LENSES = [
  ['catalog', 'Catalog', CATALOG],
  ['products', 'Products', PRODUCT],
  ['projects', 'Functions'],
];
const ROW_OUTPUTS = [
  ['customer', 'Proposal'],
  // LABEL only — the view KEY stays 'onshape' (persisted in localStorage).
  ['onshape', '3D Model', <Model3DMark size={13} key="m3d" />],
];
const ROW_CUSTOMER = [
  ['customerConfigure', 'Functions'],
  ['customer', 'Proposal'],
];
export const PROJECT_ONLY_VIEWS = ['customer', 'customerConfigure'];
export const STANDARD_HAS_NO = 'Standard is a library: pick a project in Working in to open this.';
// ⚠ CATALOG, PRODUCTS AND 3D MODEL CARRY ICONS; THE REST DO NOT. (Written when those
// three were one row, before D9; the icons stayed with the item views.)
// Alex, 2026-08-27, circling exactly these three: "can the icon be added to the
// Catalog, Product & 3D model title". These are the ITEM views — the three ways
// of looking at the same catalog — and the glyphs were reserved for this in
// icons.js on 2026-07-20 ("SYNC/VIEW_3D are reserved here... not yet wired").
// Rows 1/1b are AUDIENCE views (Internal / Customer / Proposal); marking one
// group and not the other is what makes the grouping visible at a glance, which
// is the job an icon is doing here. Giving all five icons would restore the
// undifferentiated list this replaced.
// Generated pages that describe the app to itself: /schema reads the live DB,
// /surfaces reads src/web. Neither is authored by hand, so both are always true.
const AUDIT = [
  ['/schema', 'Schema', 'the live database — tables, columns, live/dormant/legacy'],
  ['/surfaces', 'Surfaces', 'one row per job a window does, one column per view'],
];

// ⚠ THE TAB MUST NOT MOVE WHEN YOU CLICK IT. Alex, 2026-08-26, on reaching for
// 3D Model: "that makes it also the one I click shifting."
//
// The cause is one line below: active tabs render at weight 700 and inactive at
// 400. Bold text is WIDER, so switching view re-flows the whole row — the tab
// leaving active narrows, everything to its right slides left, and it slides
// while the pointer is still over it. A control that moves under the cursor as a
// direct result of being clicked makes a second click land somewhere else.
//
// The weight change STAYS. Alex is colour-blind, so weight and shape are doing
// real work here that colour cannot be asked to do alone — removing it to stop
// the shift would fix the wobble by deleting the signal.
//
// Instead each button is sized for its BOLD form always. The label renders twice:
// once visibly at the current weight, and once as a permanently-bold ghost that
// is invisible, unclickable and zero-height, present only so the grid column it
// sits in can never be narrower than the bold text. The visible label is centred
// in that reserved width, so nothing moves in either direction.
function SwitchButton({ v, lbl, icon = null, view, setView, onSelectRoot, disabledReason = null }) {
  const active = view === v;
  const disabled = !!disabledReason && !active;
  return (
    <button
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      onClick={() => (active ? onSelectRoot?.() : setView(v))}
      style={{
        background: active ? 'var(--tg-node-active-bg)' : 'transparent',
        color: active ? NAVY : 'var(--th-text-secondary)',
        // 6px sides, not 12 (2026-10-14): Catalog + Products + Functions on one row
        // measured 269px against 230px at the default tree width with 12px; with 6px
        // and a 2px gap it is 229px in the headless check (a wider font than Windows').
        // If it still wraps, the row wraps rather than clips; widen the tree.
        border: 'none', padding: '4px 6px', borderRadius: BTN_TAB_RADIUS,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
        fontWeight: active ? 700 : 400, fontSize: 'var(--fs-body)',
        // One cell, two stacked children: both land in the same grid area, so the
        // button takes the width of the WIDER — which is always the bold ghost.
        display: 'grid',
      }}
    >
      {/* The icon is INSIDE both spans, so the bold ghost still reserves the full
          width and the no-shift-on-click property survives — an icon added
          outside them would be measured in one and not the other, which is the
          same reflow bug with a new cause.
          ⚠ `icon` is a STRING for two of these and an SVG ELEMENT for the third
          (see icons.js Model3DMark for why only 3D moved). inline-flex renders
          both the same way, where the template string this used to build could
          only ever hold text — an element in it would have stringified to
          "[object Object]" rather than failing. */}
      <span style={{ gridArea: '1 / 1', display: 'inline-flex', alignItems: 'center', gap: 6 }}>{icon}{lbl}</span>
      <span aria-hidden="true" style={{ gridArea: '1 / 1', fontWeight: 700, visibility: 'hidden', height: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{icon}{lbl}</span>
    </button>
  );
}

export default function ViewSwitcher({ view, setView, onSelectRoot }) {
  // ⚠ THE AUDIT ROW IS NOT OFFERED OUTSIDE AN INTERNAL SEAT (Alex, 2026-08-27:
  // "on the production we need to remove the Schema and Surfaces link and make
  // them available in a kind of setting screen of admin type group only").
  //
  // The ROUTES were already internal-only in route-policy.js, so nobody else
  // could ever open them — what this removes is the OFFER. A link every visitor
  // can see and nobody but Alex can follow is two bad things at once: it dead-ends
  // for them, and it advertises that the app has a schema dump and a component map.
  //
  // Demo is excluded too even though a demo seat is `internal`: these pages
  // describe the app to ITSELF, which is not what the public site is for. See
  // docs/AUDIT_PAGES.md — the URLs are written down there, so hiding the buttons
  // does not mean losing them.
  const authGroup = useAuthGroup();
  const isDemo = typeof window !== 'undefined' && !!window.__INOVIU_DEMO__;
  const { customer } = useContext(NavModeContext);
  const { workingIn } = useContext(WorkingInContext);
  const projectRowReason = workingIn ? null : STANDARD_HAS_NO;
  const showAudit = !customer && !isDemo && authGroup === 'internal';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 6px 4px',
      // white, not --tg-node-l0-bg (Alex, 2026-08-20: "can the menu... background
      // be white as well" — this now sits inside CARD_STYLE's own white card, and
      // the tinted background was painting over it).
      background: 'var(--th-row-even)', borderBottom: 'var(--tg-filter-border)', flexShrink: 0,
    }}>
      {(customer ? [ROW_CUSTOMER] : [ROW_LENSES, ROW_OUTPUTS]).map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {row.map(([v, lbl, icon]) => (
            <SwitchButton key={v} v={v} lbl={lbl} icon={icon} view={view} setView={setView} onSelectRoot={onSelectRoot}
              disabledReason={v === 'customer' ? projectRowReason : null} />
          ))}
        </div>
      ))}
      {showAudit && (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ color: 'var(--th-text-secondary)', fontSize: 'var(--fs-micro)', padding: '0 2px 0 12px' }}>Audit</span>
        {AUDIT.map(([href, lbl, title]) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            style={{
              color: 'var(--th-text-secondary)', textDecoration: 'none',
              padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-small)',
            }}
          >{lbl} {LINK}</a>
        ))}
      </div>
      )}
    </div>
  );
}
